import React, { useEffect, useMemo } from 'react';
import { isItemsInCart } from '@vfs-digital-channels/ns-utils';

import { HourGlass, RocketRevamp } from 'src/assets/images';
import { ApplicationActionType } from 'src/features/common';
import { DEVICE_NAMES } from '../../tech-debt/product-details-extra';
import { isDeviceSelected } from 'src/features/common';
import { useApplication } from 'src/hooks/useApplication';
import { cn } from 'src/utils/cn';
import { Environment } from 'src/Environment';

export type SuccessPageProps = {
  onTealiumPageStart?: () => void;
  eCommerceOnlySelected?: boolean;
  contactName?: string;
  paymentGateway?: boolean;
};

export const SuccessPage = ({ onTealiumPageStart, paymentGateway }: SuccessPageProps) => {
  const { dispatch, catalogue, application } = useApplication();
  const redirectTimeOut = 15000; // 15 seconds

  const callTealiumStart = () => {
    if (onTealiumPageStart) {
      onTealiumPageStart();
    }
  };

  useEffect(() => {
    callTealiumStart();
    const stepNavigateTimeout = setTimeout(() => {
      dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
    }, redirectTimeOut);

    return () => {
      clearTimeout(stepNavigateTimeout);
    };
  }, []);

  const kwikaOrMaxSelected = useMemo(
    () =>
      !Environment.current.disabledFeatures.dpo
        ? isItemsInCart(application?.cart ?? [], [
            DEVICE_NAMES.VODAPAY_KWIKA,
            DEVICE_NAMES.VODAPAY_MAX,
          ])
        : isDeviceSelected(catalogue, [DEVICE_NAMES.VODAPAY_KWIKA, DEVICE_NAMES.VODAPAY_MAX]),
    [catalogue?.products, application.cart],
  );

  return (
    <section
      className={cn('flex flex-col text-center w-full items-center', {
        ['mt-40']: kwikaOrMaxSelected,
      })}
    >
      {paymentGateway ? (
        <>
          <img className="my-8" src={RocketRevamp} />
          <h1 className="my-4 text-4xl">Payment successful</h1>
        </>
      ) : (
        <>
          <img className="my-8" src={HourGlass} />
          <h1 className="my-4 text-4xl">You're almost there</h1>
        </>
      )}

      {kwikaOrMaxSelected ? (
        <p className="w-full text-base md:text-xl md:w-3/5 xl:w-2/5">
          Your application is almost complete. We will deduct the fees from your account once your
          FICA compliance checks have been verified.
        </p>
      ) : (
        <>
          <hr className="my-4 border-2 border-solid w-95/100 md:w-3/5 border-grey-100" />
          <p className="w-full text-base md:text-xl md:w-3/5 xl:w-2/5">
            In a moment, we will redirect you to the onboarding portal to create your profile. If
            you're ready now, please click "Setup my account".
          </p>
        </>
      )}
    </section>
  );
};
